import React, { useEffect, FC, useState } from "react";
import { Row, Col, Button } from "reactstrap";
import {
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import FormDatePicker from "../../form-components/DatePicker";
import FormTextArea from "../../form-components/TextAreaComponent";
import { Icon } from "../../../components/Component";
import { SelectOptions } from "./types";
import { DeleteOutlined } from "@ant-design/icons";
import { Table, InputNumber, Checkbox } from "antd";
import supplierApis from "../../../api/master/supplier";
import budgetApis from "../../../api/master/budget";
import contractorsApis from "../../../api/master/contractors";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import BulkBOMUpload from "./BulkBOMUpload";
import { useFormData } from "./FormDataContext";
import departments from "../../../api/master/departments";
import boqApis from "../../../api/boq/boq";
import ExportToXlsx from "../../../utils/exportToXlsx";

interface bomLine {
  boqContractorMaterialId?: string;
  categoryId: string;
  categoryName: string;
  materialId: string;
  materialName: string;
  quantity: string;
  uomId: string;
  uomName: string;
  budgetRemaining?: string;
  allocated: string;
  budgetAllocatedQty?: string;
  errorFlag?: boolean;
  errorMessage?: string[];
}

interface FormData {
  budgetCategoryId: string;
  customBudgetCategory: string;
  budgetLineName: string;
  budgetValue: string;
  startDate: any;
  endDate: any;
  bom: string;
  allocation: string;
  lineDescription: string;
  currencyCode: string;
  budgetFor: string;
  department: string;
  departmentName: string;
  contractorId: string;
  contractorName: string;
  bomLines: bomLine[];
  materialCategoryOptions: any;
}

interface AddNewBudgetLineFormProps {
  budgetStartDate?: string;
  budgetEndDate?: string;
  materialCategoryOptions?: any[];
  currentMaterial?: any;
  onSave: (data: any) => void;
  onCancel: () => void;
}

const AddNewBudgetLineForm: FC<AddNewBudgetLineFormProps> = ({
  budgetStartDate,
  budgetEndDate,
  materialCategoryOptions,
  currentMaterial,
  onSave,
  onCancel,
}) => {
  const { formData, updateFormData } = useFormData();

  const methods = useForm<FormData>({
    defaultValues: {
      budgetCategoryId: "",
      customBudgetCategory: "",
      budgetLineName: "",
      budgetValue: "",
      startDate: "",
      endDate: "",
      bom: "",
      allocation: "",
      lineDescription: "",
      currencyCode: "",
      budgetFor: "",
      department: "",
      departmentName: "",
      contractorId: "",
      contractorName: "",
      bomLines: [],
    },
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = methods;
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "bomLines",
  });

  const billOfMaterialWatch = watch("bom");
  const budgetCategoryIdWatch = watch("budgetCategoryId");
  const budgetForWatch = watch("budgetFor");
  const allocationTypeWatch = watch("allocation");
  const bomLinesWatch = watch("bomLines");

  const [uomOptions, setUomOptions] = useState<SelectOptions[]>([]);
  const [materialData, setMaterialData] = useState<any>();
  const [materialOptions, setMaterialOptions] = useState<SelectOptions[]>([]);
  const [budgetCategoryOptions, setBudgetCategoryOptions] = useState<
    SelectOptions[]
  >([]);
  const [departmentOptions, setDepartmentOptions] = useState<SelectOptions[]>([]);
  const [contractorOptions, setContractorOptions] = useState<SelectOptions[]>([]);
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [selectedBomRows, setSelectedBomRows] = useState<string[]>([]);
  const [uploadedCount, setUploadedCount] = useState<number>(0);
  const [showAllocationError, setShowAllocationError] = useState(false);

  const removeSelectedBomRows = () => {
    if (selectedBomRows.length === 0) return;
    const indexesToRemove = fields
      .map((field, index) => (selectedBomRows.includes(field.id) ? index : -1))
      .filter((i) => i !== -1)
      .sort((a, b) => b - a);
    indexesToRemove.forEach((i) => remove(i));
    setSelectedBomRows([]);
  };

  const handleBomFieldChange = (index: number, fieldName: string, value: any) => {
    setValue(`bomLines.${index}.${fieldName}` as any, value);
  };

  const bomColumns: any[] = [
    {
      title: (
        <Checkbox
          checked={fields.length > 0 && selectedBomRows.length === fields.length}
          indeterminate={selectedBomRows.length > 0 && selectedBomRows.length < fields.length}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedBomRows(fields.map((f) => f.id));
            } else {
              setSelectedBomRows([]);
            }
          }}
        />
      ),
      dataIndex: "select",
      key: "select",
      width: 32,
      align: "center" as const,
      className: "px-0",
      render: (_: any, record: any) => (
        <Checkbox
          checked={selectedBomRows.includes(record.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedBomRows((prev) => [...prev, record.id]);
            } else {
              setSelectedBomRows((prev) => prev.filter((id) => id !== record.id));
            }
          }}
        />
      ),
    },
    {
      title: "Category",
      dataIndex: "categoryName",
      key: "categoryName",
      width: "20%",
    },
    {
      title: "Material Name",
      dataIndex: "materialName",
      key: "materialName",
      width: "20%",
    },
    {
      title: "UOM",
      dataIndex: "uomName",
      key: "uomName",
      width: "10%",
    },
    {
      title: "Available Qty",
      dataIndex: "budgetRemaining",
      key: "budgetRemaining",
      width: "12%",
    },
    {
      title: <span>Qty <span style={{ color: "red" }}>*</span></span>,
      dataIndex: "quantity",
      key: "quantity",
      width: "12%",
      render: (_: any, record: any) => (
        <InputNumber
          min={1}
          placeholder="0"
          precision={2}
          controls={false}
          parser={(value: any) => value?.replace(/[^0-9.]/g, "").replace(/(\..*?)\..*/g, "$1") || ""}
          formatter={(value: any) => {
            const str = `${value}`;
            if (str.includes(".")) {
              const [int, dec] = str.split(".");
              return `${int}.${(dec || "").slice(0, 2)}`;
            }
            return str;
          }}
          onKeyDown={(e: any) => {
            if (!/[0-9.]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End"].includes(e.key)) {
              e.preventDefault();
            }
            if (e.key === "." && e.target.value.includes(".")) {
              e.preventDefault();
            }
          }}
          value={record.quantity ? Number(record.quantity) : undefined}
          style={{ width: "100%" }}
          onChange={(value) => {
            if (value == null || value <= 0) {
              handleBomFieldChange(record._index, "quantity", "");
              return;
            }
            const maxQty = record.budgetRemaining ? Number(record.budgetRemaining) : undefined;
            const rounded = Math.round(value * 100) / 100;
            const clamped = maxQty != null ? Math.min(Math.max(0.01, rounded), maxQty) : Math.max(0.01, rounded);
            handleBomFieldChange(record._index, "quantity", clamped.toString());
          }}
        />
      ),
    },
    {
      title: <span>Allocation {allocationTypeWatch === "percentage" ? "(%)" : allocationTypeWatch === "value" ? "(Value)" : ""} <span style={{ color: "red" }}>*</span></span>,
      dataIndex: "allocated",
      key: "allocated",
      width: "15%",
      render: (_: any, record: any) => (
        <InputNumber
          min={0}
          max={allocationTypeWatch === "percentage" ? 100 : undefined}
          precision={0}
          placeholder="0"
          value={record.allocated ? Number(record.allocated.toString().replace(/,/g, "")) : undefined}
          style={{ width: "100%" }}
          controls={false}
          parser={(value: any) => value?.replace(/[^0-9]/g, "") || ""}
          formatter={(value: any) => `${value}`.replace(/[^0-9]/g, "")}
          onKeyDown={(e: any) => {
            if (!/[0-9]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End"].includes(e.key)) {
              e.preventDefault();
            }
          }}
          onChange={(value) => handleBomFieldChange(record._index, "allocated", value?.toString() || "")}
        />
      ),
    },
    {
      title: "",
      key: "actions",
      width: 40,
      align: "center" as const,
      render: (_: any, record: any) => (
        <DeleteOutlined
          onClick={() => {
            remove(record._index);
            setSelectedBomRows((prev) => prev.filter((id) => id !== record.id));
          }}
          style={{ color: "#ff4d4f", fontSize: 16, cursor: "pointer" }}
        />
      ),
    },
  ];

  const isOthersCategory = (categoryId: string) => {
    const selectedCategory = budgetCategoryOptions.find(
      (option) => option.value === categoryId
    );
    return selectedCategory?.label?.toLowerCase() === "others";
  };

  const fetchBudgetCategories = async () => {
    try {
      const response = await budgetApis.fetchBudgetCategoryList();

      if (response.status) {
        const categories = response.response;

        if (Array.isArray(categories)) {
          const categoryOptions = categories.map((item: any) => ({
            label: item.budgetCategoryName,
            value: item.budgetCategoryId.toString(),
          }));
          setBudgetCategoryOptions(categoryOptions);
        }
      }
    } catch (error) {
      console.log("Error fetching budget categories:", error);
    }
  };

  const fetchContractors = async () => {
    try {
      const siteId = formData.budgetHeader?.site;
      if (!siteId) return;
      const data = await contractorsApis.fetchContractorBySiteId({ id: siteId });
      if (data) {
        const options = data.data?.map((item: any) => ({
          label: item.companyName,
          value: item.contractorID.toString(),
        }));
        setContractorOptions(options || []);
      }
    } catch (error) {
      console.log("Error fetching contractors:", error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const { data, status } = await departments.fetchDeptData();
      if (status) {
        const departmentOptions = data.map((item: any) => ({
          label: item.deptName,
          value: item.deptID.toString(),
        }));
        setDepartmentOptions(departmentOptions);
      }
    } catch (error) {
      console.log("Error fetching departments:", error);
    }
  };

  useEffect(() => {
    if (currentMaterial) {
      reset({
        budgetCategoryId: currentMaterial.budgetCategoryId || "",
        customBudgetCategory: currentMaterial.customBudgetCategory || "",
        budgetLineName: currentMaterial.budgetLineName,
        budgetValue: currentMaterial.budgetValue,
        startDate: dayjs(currentMaterial.startDate),
        endDate: dayjs(currentMaterial.endDate),
        bom: currentMaterial.bom || "N",
        currencyCode: currentMaterial.currencyCode,
        allocation: currentMaterial.allocation,
        lineDescription: currentMaterial.lineDescription,
        budgetFor: currentMaterial.budgetFor || "Material",
        contractorId: currentMaterial.contractorId || "",
        contractorName: currentMaterial.contractorName || "",
        department: currentMaterial.department?.toString() || "",
        departmentName: currentMaterial.departmentName || "",
        bomLines: [],
      });
      setShowCustomCategory(isOthersCategory(currentMaterial.budgetCategoryId));
      setShowAllocationError(false);
      // When editing with BOM, fetch all BOQ lines and merge with saved ones
      if (currentMaterial.bom === "Y" && currentMaterial.contractorId) {
        fetchContractorBoqLines(currentMaterial.contractorId, currentMaterial.bomLines || []);
      }
    } else {
      reset({
        budgetCategoryId: "",
        customBudgetCategory: "",
        budgetLineName: "",
        budgetValue: "",
        startDate: "",
        endDate: "",
        bom: "",
        allocation: "",
        lineDescription: "",
        currencyCode: "",
        budgetFor: "",
        contractorId: "",
        contractorName: "",
        bomLines: [],
      });
      setShowCustomCategory(false);
      setShowAllocationError(false);
    }
  }, [currentMaterial, reset]);

  useEffect(() => {
    // Skip auto-appending empty row when budgetFor is "Material" — API will populate BOM lines
    if (budgetForWatch === "Material") return;

    if (billOfMaterialWatch === "Y" && fields.length === 0) {
      append({
        categoryId: "",
        categoryName: "",
        materialId: "",
        materialName: "",
        quantity: "",
        uomId: "",
        uomName: "",
        allocated: "",
      });
    } else if (billOfMaterialWatch === "Y" && fields.length > 0) {
      (async () => {
        const { data, materialOption }: any = await fetchMaterials(
          fields[fields.length - 1].categoryId,
          fields
        );
        await fetchUom(
          fields[fields.length - 1].materialId,
          data,
          fields.length - 1
        );
      })();
    }
  }, [billOfMaterialWatch, append, fields.length]);

  useEffect(() => {
    fetchBudgetCategories();
    fetchDepartments();
    fetchContractors();
  }, []);

  // Reset BOM lines and contractor when site changes in general details
  const prevSiteRef = React.useRef(formData.budgetHeader?.site);
  useEffect(() => {
    const currentSite = formData.budgetHeader?.site;
    if (prevSiteRef.current && currentSite && prevSiteRef.current !== currentSite) {
      // Site changed — reset contractor, BOM lines, and selections
      setValue("contractorId", "");
      setValue("contractorName", "");
      reset({
        ...methods.getValues(),
        contractorId: "",
        contractorName: "",
        bomLines: [],
      });
      setSelectedBomRows([]);
      // Re-fetch contractors for new site
      fetchContractors();
    }
    prevSiteRef.current = currentSite;
  }, [formData.budgetHeader?.site]);

  useEffect(() => {
    const isOthersSelected = isOthersCategory(budgetCategoryIdWatch);
    setShowCustomCategory(isOthersSelected);
    if (!isOthersSelected) {
      setValue("customBudgetCategory", "");
    }
  }, [budgetCategoryIdWatch, budgetCategoryOptions, setValue]);

  const fetchContractorBoqLines = async (contractorId: string, existingBomLines?: any[]) => {
    try {
      const siteId = formData.budgetHeader?.site;
      if (!siteId || !contractorId) return;
      const response = await boqApis.fetchContractorBoqDropdown({
        siteIds: siteId.toString(),
        contractorIds: contractorId,
      });
      const data = response?.data || response?.response || [];
      if (Array.isArray(data) && data.length > 0) {
        // Build a set of existing boqContractorMaterialIds for quick lookup (materialId is not unique — same material can have different UOMs)
        const existingBoqIds = new Set(
          (existingBomLines || []).map((line: any) => line.boqContractorMaterialId?.toString())
        );

        // Build complete bomLines array: saved lines first, then remaining API lines
        const allBomLines: any[] = [];

        // Add existing/saved BOM lines first (these will be pre-selected)
        const savedCount = existingBomLines?.length || 0;
        (existingBomLines || []).forEach((line: any) => {
          const apiItem = data.find(
            (item: any) =>
              item.boqContractorMaterialId?.toString() === line.boqContractorMaterialId?.toString()
          );
          allBomLines.push({
            boqContractorMaterialId: line.boqContractorMaterialId || apiItem?.boqContractorMaterialId?.toString() || "",
            categoryId: line.categoryId?.toString() || "",
            categoryName: line.categoryName || "",
            materialId: line.materialId?.toString() || "",
            materialName: line.materialName || "",
            quantity: line.quantity?.toString() || "",
            uomId: line.uomId?.toString() || "",
            uomName: line.uomName || "",
            budgetRemaining: apiItem?.budgetRemainingQty?.toString() || apiItem?.budgetRemaining?.toString() || "",
            allocated: line.allocated?.toString() || "",
            budgetAllocatedQty: line.budgetAllocatedQty?.toString() || apiItem?.budgetAllocatedQty?.toString() || "",
          });
        });

        // Add remaining BOQ lines that are NOT in existing saved lines
        data.forEach((item: any) => {
          const boqId = item.boqContractorMaterialId?.toString() || "";
          const matId = item.materialId?.toString() || item.materialID?.toString() || "";
          if (boqId && !existingBoqIds.has(boqId)) {
            allBomLines.push({
              boqContractorMaterialId: boqId || item.materialID?.toString() || "",
              categoryId: item.categoryId?.toString() || "",
              categoryName: item.categoryName || "",
              materialId: matId,
              materialName: item.materialName || item.productName || "",
              quantity: "",
              uomId: item.uomId?.toString() || item.primaryUomId?.toString() || "",
              uomName: item.uomName || item.primaryUomName || "",
              budgetRemaining: item.budgetRemainingQty?.toString() || item.budgetRemaining?.toString() || "",
              allocated: "",
              budgetAllocatedQty: item.budgetAllocatedQty?.toString() || "",
            });
          }
        });

        // Set all BOM lines at once using replace to avoid race conditions
        replace(allBomLines);

        // Auto-select the saved BOM lines
        if (savedCount > 0) {
          setUploadedCount(savedCount);
        }
      }
    } catch (error) {
      console.log("Error fetching contractor BOQ lines:", error);
    }
  };

  useEffect(() => {
    if (budgetForWatch === "Material") {
      // User will manually select BOM yes/no; BOM lines populated when contractor is selected
    } else if (budgetForWatch === "Service/Work") {
      setValue("bom", "N");
      reset({
        ...methods.getValues(),
        bom: "N",
        allocation: "",
        bomLines: [],
      });
    }
  }, [budgetForWatch]);

  const fetchUom = async (materialId: any, materialData: any, index: any) => {
    if (!materialId) return [];
    try {
      const materialUom = materialData?.find(
        (item: any) => item.materialId == materialId
      );
      if (!materialUom) return;
      const _uomOptions = [
        {
          value: materialUom?.primaryUomId,
          label: materialUom?.PrimaryUomName,
        },
      ];
      if (
        materialUom?.secondaryUomId &&
        materialUom?.primaryUomId != materialUom?.secondaryUomId &&
        materialUom?.PrimaryUomName != materialUom?.secondaryUomName
      ) {
        _uomOptions.push({
          value: materialUom?.secondaryUomId,
          label: materialUom?.secondaryUomName,
        });
      }
      setUomOptions(_uomOptions);
      return _uomOptions;
    } catch (error) {
      console.log(error);
    }
  };

  const fetchMaterials = async (categoryId: any, fieldsArray?: any) => {
    if (!categoryId) return { data: [], materialOption: [] };
    try {
      const { data, status } = await supplierApis.fetchCategoryProducts(
        categoryId
      );
      if (status) {
        const materialOption = data.map((item: any) => ({
          label: item?.materialName,
          value: item?.materialId?.toString(),
        }));

        // Only filter if fieldsArray is provided and is an array
        let filteredOptions = materialOption;
        if (Array.isArray(fieldsArray)) {
          const createdMaterialIds = fieldsArray.map(
            (field: any) => field?.materialId
          );
          filteredOptions = materialOption?.filter(
            (option: any) =>
              !createdMaterialIds || !createdMaterialIds.includes(option.value)
          );
        }

        setMaterialData(data);
        setMaterialOptions(filteredOptions);
        return {
          data,
          materialOption,
        };
      }
    } catch (error) {
      console.log(error);
      return {};
    }
  };

  const handleBulkUpload = (uploadedBomLines: any[]) => {
    // Clear existing BOM lines first
    reset({
      ...methods.getValues(),
      bomLines: [],
    });

    // Track how many are from the upload (matched lines come first)
    const count = uploadedBomLines.filter((b: any) => b._fromUpload).length;
    setUploadedCount(count);

    // Add all uploaded BOM lines - they already have materialName and uomName from the API response
    uploadedBomLines.forEach((bomLine: any) => {
      append({
        boqContractorMaterialId: bomLine.boqContractorMaterialId || "",
        categoryId: bomLine.categoryId,
        categoryName: bomLine.categoryName,
        materialId: bomLine.materialId,
        materialName: bomLine.materialName,
        quantity: bomLine.quantity,
        uomId: bomLine.uomId,
        uomName: bomLine.uomName,
        budgetRemaining: bomLine.budgetRemaining || "",
        allocated: bomLine.allocated,
        budgetAllocatedQty: bomLine.budgetAllocatedQty || "",
        errorFlag: bomLine.errorFlag,
        errorMessage: bomLine.errorMessage,
      });
    });
  };

  // Auto-select uploaded BOM lines once fields are updated
  useEffect(() => {
    if (uploadedCount > 0 && fields.length >= uploadedCount) {
      const uploadedFieldIds = fields.slice(0, uploadedCount).map((f) => f.id);
      setSelectedBomRows(uploadedFieldIds);
      setUploadedCount(0);
    }
  }, [fields, uploadedCount]);

  // Reset allocation values when BOM Allocation type changes
  const prevAllocationType = React.useRef(allocationTypeWatch);
  useEffect(() => {
    if (prevAllocationType.current && allocationTypeWatch && prevAllocationType.current !== allocationTypeWatch && fields.length > 0) {
      const currentValues = methods.getValues();
      const resetBomLines = currentValues.bomLines.map((line: any) => ({
        ...line,
        allocated: "",
      }));
      reset({ ...currentValues, bomLines: resetBomLines });
    }
    prevAllocationType.current = allocationTypeWatch;
  }, [allocationTypeWatch]);

  const submitAction = (data: FormData) => {
    try {
      setShowAllocationError(true);
      if (!data.contractorId) {
        methods.setError("contractorId", {
          type: "required",
          message: "This field is required",
        });
        return;
      }
      // Only selected BOM lines should be saved
      if (data.bom === "Y" && fields.length > 0) {
        if (selectedBomRows.length === 0) {
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: "Please select at least one BOM line before adding the budget line.",
          });
          return;
        }
        const selectedIndices = fields
          .map((field, index) => (selectedBomRows.includes(field.id) ? index : -1))
          .filter((i) => i !== -1);
        data.bomLines = selectedIndices.map((i) => data.bomLines[i]);
      }

      // Clean up bomLines payload - remove display-only fields and empty uom fields
      if (data.bomLines) {
        data.bomLines = data.bomLines.map((line: any) => {
          const { budgetRemaining, _fromUpload, ...rest } = line;
          if (!rest.uomId) {
            delete rest.uomId;
            delete rest.uomName;
          }
          return rest;
        });
      }

      const totalAllocated = data.bomLines.reduce((sum, item) => {
        return (
          sum +
          parseFloat(item?.allocated?.toString()?.replace(/,/g, "") || "0")
        );
      }, 0);
      const budgetValue = parseFloat(data.budgetValue?.toString()?.replace(/,/g, "") || "0");
      if (data.allocation === "value") {
        if (data.bom === "Y" && totalAllocated !== budgetValue) {
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: totalAllocated > budgetValue
              ? "Total Allocated value in BOM Lines cannot exceed the Budget Value."
              : "Total Allocated value in BOM Lines must be equal to the Budget Value.",
          });
          return;
        }
      } else if (data.allocation === "percentage") {
        const totalPercentage = totalAllocated;
        if (data.bom === "Y" && totalPercentage > 100) {
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: "Total Allocated % in BOM Lines cannot exceed more than 100%.",
          });
          return;
        }
        if (data.bom === "Y" && totalPercentage < 100) {
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: "Total Allocated % in BOM Lines must be equal to 100%.",
          });
          return;
        }
      }

      // Handle budget category logic
      if (
        isOthersCategory(data.budgetCategoryId) &&
        !data.customBudgetCategory.trim()
      ) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Please enter a custom budget category name.",
        });
        return;
      }

      data.startDate = dayjs(data.startDate).format("YYYY-MM-DD");
      data.endDate = dayjs(data.endDate).format("YYYY-MM-DD");
      data.currencyCode = "INR";

      // Prepare data for Redux
      const budgetLineData = {
        ...data,
        budgetCategoryId: data.budgetCategoryId,
        customBudgetCategory: isOthersCategory(data.budgetCategoryId)
          ? data.customBudgetCategory
          : undefined,
        key: currentMaterial?.key || `${Date.now()}`,
        status: currentMaterial?.status || "Draft",
        budgetId: currentMaterial?.budgetId || "",
        budgetLineId: currentMaterial?.budgetLineId || "",
      };

      // Update FormDataContext
      const currentMaterials = formData.materialsData || [];
      let updatedMaterials;

      if (currentMaterial) {
        // Update existing material
        updatedMaterials = currentMaterials.map((material: any) =>
          material.key === budgetLineData.key ? budgetLineData : material
        );
      } else {
        // Add new material
        updatedMaterials = [...currentMaterials, budgetLineData];
      }

      updateFormData("materialsData", updatedMaterials);

      // Call parent callback
      onSave(budgetLineData);
    } catch (error) {
      console.log("Error in submitAction", error);
    }
  };

  const exportBomLines = () => {
    // If user has selected rows, export only selected; otherwise export all
    const indicesToExport = selectedBomRows.length > 0
      ? fields.map((field, index) => (selectedBomRows.includes(field.id) ? index : -1)).filter((i) => i !== -1)
      : fields.map((_, index) => index);

    const bomLinesData = indicesToExport.map((index) => {
      const line = methods.getValues(`bomLines.${index}`);
      return {
        "Material ID": line.materialId || "",
        "Category Name": line.categoryName || "",
        "Material Name": line.materialName || "",
        "UOM": line.uomName || "",
        "Budget Remaining": line.budgetRemaining || "",
        "Quantity": line.quantity || "",
        "Allocation": line.allocated || "",
      };
    });
    if (bomLinesData.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Data",
        text: "No BOM lines available to export.",
      });
      return;
    }
    ExportToXlsx(bomLinesData, "BOM_Lines");
  };

  const startDate = watch("startDate");

  return (
    <div>

      <FormProvider {...methods}>
        <div>
          <Row className="gy-4">
            <Col lg="6">
              <FormInput
                name="budgetLineName"
                label="Budget Line Name"
                required={true}
                placeholder="Enter Budget Line Name"
              />
            </Col>
            <Col lg="6">
              <FormSelect
                name="budgetFor"
                label="Budget For"
                options={[
                  { label: "Material", value: "Material" },
                  { label: "Service/Work", value: "Service/Work" },
                ]}
                required={true}
                placeholder="Select Budget For"
              />
            </Col>

            <Col lg="6">
              <FormInput
                min={1}
                name="budgetValue"
                type="text"
                label="Budget Value"
                required={true}
                placeholder="Enter Budget Value"
                pattern={/^[0-9,]+$/}
                onInput={(e: any) => {
                  const val = e.target.value.replace(/[^\d.]/g, "");
                  if (val.length > 0 && !/^[0-9]+(\.[0-9]+)?$/.test(val)) {
                    e.target.value = val.replace(/[^\d.]/g, "");
                  } else {
                    const n = Number(val);
                    const formattedValue = isNaN(n)
                      ? ""
                      : n.toLocaleString("en-IN");
                    e.target.value = formattedValue;
                  }
                }}
              />
            </Col>
            <Col lg="6">
              <FormDatePicker
                name="startDate"
                label="Planned Start Date"
                placeholder="Select Start Date"
                required={true}
                minDate={new Date(budgetStartDate || "")}
                maxDate={new Date(budgetEndDate || "")}
              />
            </Col>
            <Col lg="6">
              <FormDatePicker
                name="endDate"
                label="Planned End Date"
                placeholder="Select End Date"
                required={true}
                minDate={startDate}
                maxDate={new Date(budgetEndDate || "")}
                disabled={!startDate}
              />
            </Col>
            <Col lg="6">
              <FormSelect
                name="budgetCategoryId"
                label="Budget Category"
                options={budgetCategoryOptions}
                required={true}
                placeholder="Select Budget Category"
              />
            </Col>
            {showCustomCategory && (
              <Col lg="6">
                <FormInput
                  name="customBudgetCategory"
                  label="Budget Category Name"
                  required={true}
                  placeholder="Enter Budget Category Name"
                />
              </Col>
            )}
            <Col lg="6">
              <FormSelect
                name="contractorId"
                label="Contractor"
                options={contractorOptions}
                required={true}
                placeholder="Select Contractor"
                onChange={(value) => {
                  const contractorName =
                    contractorOptions.find(
                      (opt: any) => opt.value === value
                    )?.label || "";
                  setValue("contractorName", contractorName);
                  if (value) {
                    methods.clearErrors("contractorId");
                  }
                  if (budgetForWatch === "Material" && value) {
                    fetchContractorBoqLines(value);
                  }
                }}
              />
            </Col>
            <Col lg="6">
              <FormSelect
                name="department"
                label="Department"
                options={departmentOptions}
                required={true}
                placeholder="Select Department"
                onChange={(value) => {
                  const deptName = departmentOptions.find(
                    (dept: any) => dept.value === value
                  )?.label || "";
                  setValue("departmentName", deptName);
                }}
              />
            </Col>
            {budgetForWatch === "Material" && (
              <Col lg="6">
                <FormSelect
                  name="bom"
                  label="Bill Of Material (BOM)"
                  options={[
                    { label: "Yes", value: "Y" },
                    { label: "No", value: "N" },
                  ]}
                  required={true}
                  placeholder="Select BOM"
                  onChange={(e) => {
                    if (e === "Y" && fields.length === 0) {
                      const contractorId = methods.getValues("contractorId");
                      if (contractorId) {
                        fetchContractorBoqLines(contractorId);
                      }
                    } else if (e === "N") {
                      reset({
                        ...methods.getValues(),
                        bomLines: [],
                      });
                    }
                  }}
                />
              </Col>
            )}
            {billOfMaterialWatch === "Y" && (
              <Col lg="6">
                <FormSelect
                  name="allocation"
                  label="BOM Allocation"
                  options={[
                    { label: "Value", value: "value" },
                    { label: "%", value: "percentage" },
                  ]}
                  required={true}
                  placeholder="Select BOM Allocation"
                />
              </Col>
            )}
            <Col lg="12">
              <FormTextArea
                name="lineDescription"
                label="Budget Line Description"
                required={true}
                placeholder="Enter Budget Line Description"
              />
            </Col>
          </Row>

          {billOfMaterialWatch === "Y" && (
            <div style={{ marginTop: "20px" }}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="card-title mb-0">BOM Lines</h6>
                <div className="d-flex align-items-center gap-2">
                  <BulkBOMUpload
                    onUpload={handleBulkUpload}
                    materialCategoryOptions={materialCategoryOptions || []}
                    allocation={watch("allocation")}
                    budgetValue={parseFloat(watch("budgetValue")?.toString()?.replace(/,/g, '') || '0') || 0}
                    existingBomLines={fields}
                    siteId={formData.budgetHeader?.site?.toString()}
                    contractorId={watch("contractorId")}
                    disabled={!watch("allocation") || !watch("contractorId") || !watch("bom")}
                  />
                  <Button
                    size="sm"
                    color="light"
                    className="btn btn-white btn-outline-light"
                    onClick={exportBomLines}
                  >
                    <Icon name="download-cloud" />
                    <span>Export</span>
                  </Button>
                  {selectedBomRows.length > 0 && (
                    <Button
                      size="sm"
                      color="danger"
                      onClick={removeSelectedBomRows}
                    >
                      <DeleteOutlined style={{ marginRight: 6 }} />
                      Remove Selected ({selectedBomRows.length})
                    </Button>
                  )}
                </div>
              </div>

              <div style={{
                border: "1px solid #f0f0f0",
                borderRadius: "8px",
                overflow: "auto",
              }}>
                <Table
                  columns={bomColumns}
                  dataSource={fields.map((field, index) => ({
                    ...field,
                    ...(bomLinesWatch?.[index] || {}),
                    key: field.id,
                    _index: index,
                  })).sort((a, b) => {
                    const aSelected = selectedBomRows.includes(a.id) ? 0 : 1;
                    const bSelected = selectedBomRows.includes(b.id) ? 0 : 1;
                    return aSelected - bSelected;
                  })}
                  rowKey="key"
                  pagination={false}
                  size="middle"
                  bordered={false}
                  scroll={{ x: "max-content" }}
                  locale={{ emptyText: "No BOM lines added yet" }}
                />
              </div>

              {showAllocationError && selectedBomRows.length > 0 && (() => {
                const selectedIndices = fields
                  .map((field, idx) => (selectedBomRows.includes(field.id) ? idx : -1))
                  .filter((i) => i !== -1);
                const totalAllocated = selectedIndices.reduce((sum, idx) => {
                  const val = methods.getValues(`bomLines.${idx}.allocated`);
                  return sum + (parseFloat(val?.toString()?.replace(/,/g, "") || "0") || 0);
                }, 0);
                const bv = parseFloat(watch("budgetValue")?.toString()?.replace(/,/g, "") || "0") || 0;
                const isValue = allocationTypeWatch === "value";
                const isPercentage = allocationTypeWatch === "percentage";
                const hasError = (isValue && totalAllocated !== bv) || (isPercentage && totalAllocated !== 100);

                return hasError ? (
                  <div style={{ color: "#ff4d4f", fontSize: 13, marginTop: 8 }}>
                    {isValue
                      ? totalAllocated > bv
                        ? `Selected BOM lines total allocation (${totalAllocated.toLocaleString()}) exceeds Budget Value (${bv.toLocaleString()})`
                        : `Selected BOM lines total allocation (${totalAllocated.toLocaleString()}) is below Budget Value (${bv.toLocaleString()})`
                      : totalAllocated > 100
                        ? `Selected BOM lines total allocation (${totalAllocated}%) exceeds 100%`
                        : `Selected BOM lines total allocation (${totalAllocated}%) is below 100%`}
                  </div>
                ) : null;
              })()}
            </div>
          )}

          <Row className="mt-4">
            <Col size="12">
              <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                <li>
                  <Button
                    className="btn btn-primary btn-md"
                    onClick={handleSubmit(submitAction)}
                    color="primary"
                  >
                    <span>{currentMaterial ? "Update" : "Add"} Budget Line</span>
                  </Button>
                </li>
                <li>
                  <Button
                    onClick={onCancel}
                    className="btn btn-secondary btn-md"
                    type="button"
                  >
                    <span>Cancel</span>
                  </Button>
                </li>
              </ul>
            </Col>
          </Row>
        </div>
      </FormProvider>
    </div>
  );
};

export default AddNewBudgetLineForm;